import {FormProvider, useForm} from 'react-hook-form';
import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import FormResult from 'components/Form/FormResult';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useBackend} from 'state/backend';
import {c, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import useReactivateEmployee from './services';
import {RevokedBackendData} from './types';

const Revoked = () => {
    const {
        data: {navigationTabs, partnerIsSfConnected, revokedEmployees},
    } = useBackend<RevokedBackendData>();
    const methods = useForm();
    const {mutateAsync} = useReactivateEmployee();

    const {paginatedData, ...pagination} = usePaginatedData(
        revokedEmployees,
        10,
    );

    const handleReactivate = (mid: string) => {
        mutateAsync({mid})
            .then(
                onApiSuccess({
                    onSuccess: () => setUrl(window.Urls.employee_list_view()),
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <div>
            <DjangoMessages />
            <FormProvider {...methods}>
                <NavigationTabs.Team
                    className="my-4"
                    selection="revoked"
                    {...navigationTabs}
                />
                <FormResult />
                <SectionHeader title={t`Revoked Team Members`} />
                {paginatedData.length <= 0 && (
                    <p>{t`No team members have been revoked.`}</p>
                )}
                {paginatedData.length > 0 && (
                    <>
                        <p>{t`The following team members no longer have access to this company.`}</p>
                        <TableStatic className="mt-4" id="revoked-employees">
                            <TableStatic.Header>
                                <TableStatic.HeaderRow>
                                    <TableStatic.Column>
                                        {c('Revoked').t`Name`}
                                    </TableStatic.Column>
                                    <TableStatic.Column>
                                        {c('Revoked').t`Start Date`}
                                    </TableStatic.Column>
                                    <TableStatic.Column>
                                        {c('Revoked').t`End Date`}
                                    </TableStatic.Column>
                                    <TableStatic.Column />
                                </TableStatic.HeaderRow>
                            </TableStatic.Header>
                            <TableStatic.Body>
                                {paginatedData.map((employee) => (
                                    <TableStatic.Row
                                        key={employee?.mid || Math.random()}
                                        className={
                                            employee ? '' : 'bg-grey-100'
                                        }
                                    >
                                        {!employee && (
                                            <>
                                                <TableStatic.Cell>
                                                    &nbsp;
                                                </TableStatic.Cell>
                                                <TableStatic.Cell>
                                                    &nbsp;
                                                </TableStatic.Cell>
                                                <TableStatic.Cell>
                                                    &nbsp;
                                                </TableStatic.Cell>
                                                <TableStatic.Cell>
                                                    &nbsp;
                                                </TableStatic.Cell>
                                            </>
                                        )}
                                        {employee && (
                                            <>
                                                <TableStatic.Cell>
                                                    <a
                                                        href={
                                                            employee.getAbsoluteUrl
                                                        }
                                                    >
                                                        {employee.name}
                                                    </a>
                                                </TableStatic.Cell>
                                                <TableStatic.Cell>
                                                    {employee.startDate}
                                                </TableStatic.Cell>
                                                <TableStatic.Cell>
                                                    {employee.endDate}
                                                </TableStatic.Cell>
                                                <TableStatic.Cell className="text-right">
                                                    {!partnerIsSfConnected && (
                                                        <TooltipWrapper
                                                            message={
                                                                employee
                                                                    .canReactivate
                                                                    .failureReason
                                                            }
                                                        >
                                                            <Button
                                                                disabled={
                                                                    !employee
                                                                        .canReactivate
                                                                        .allowed
                                                                }
                                                                kind="secondary"
                                                                onClick={() =>
                                                                    handleReactivate(
                                                                        employee.mid,
                                                                    )
                                                                }
                                                                size="xs"
                                                            >
                                                                {t`Reactivate`}
                                                            </Button>
                                                        </TooltipWrapper>
                                                    )}
                                                </TableStatic.Cell>
                                            </>
                                        )}
                                    </TableStatic.Row>
                                ))}
                            </TableStatic.Body>
                        </TableStatic>
                        <TablePagination
                            className="mt-4"
                            hideForSinglePage={true}
                            id="revoked-employees-pagination"
                            {...pagination}
                        />
                    </>
                )}
            </FormProvider>
        </div>
    );
};

export default Revoked;
